import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Drawer } from '~/shared/components/Drawer';
import { Anchor } from '~/shared/components/Anchor/Anchor';
import { Button } from '~/shared/components/Button/Button';
import Image from 'next/image';
import Typography from '~/shared/components/Typography/Typography';
import { QRCodeSVG } from 'qrcode.react';
import { useScrollLock } from '~/shared/hooks/useScrollLock';
import { notifyInfo } from '~/shared/utils/errorBoundary/toast';
import { useTranslation } from '~/shared/utils/translation';
import type { TranslationKey } from '~/lib/data-contract';
import ShareIcon from '~/icons/share.svg';
import styles from './VouchersCardDrawer.module.scss';

export interface VoucherDetails {
    id: string;
    title: string;
    description: string;
    expiryDate: string;
    badgeText?: string;
    imageSrc?: string;
    imageAlt?: string;
    promoCode?: string;
    barcode?: string;
}

interface VouchersCardDrawerProps {
    open: boolean;
    onClose: () => void;
    voucher: VoucherDetails;
    scrollLock?: boolean;
}

const VouchersCardDrawer: React.FC<VouchersCardDrawerProps> = ({
    open,
    onClose,
    voucher,
    scrollLock = true,
}) => {
    const { lock, unlock } = useScrollLock();
    const { translate } = useTranslation();
    const [copied, setCopied] = useState(false);

    // Apply scroll lock when drawer opens
    useEffect(() => {
        if (open && scrollLock) {
            lock();
        }

        return () => {
            if (scrollLock) {
                unlock();
            }
        };
    }, [open, scrollLock, lock, unlock]);

    // Reset copied state when drawer closes
    useEffect(() => {
        if (!open) {
            setCopied(false);
        }
    }, [open]);

    const handleShareVoucher = async () => {
        // A rudimentary basic share function for now
        const shareText = `${voucher.title}\n\n${voucher.description}\n\n${
            voucher.promoCode ? `Rabatkode: ${voucher.promoCode}\n` : ''
        }Gyldig til: ${voucher.expiryDate}\n\nSe mere på imerco.dk`;

        const shareData = {
            title: voucher.title,
            text: shareText,
            // TODO: Include current page URL; url: window.location.href, // Optional: include current page URL
        };

        try {
            if (navigator.share && navigator.canShare?.(shareData)) {
                await navigator.share(shareData);
                notifyInfo(translate('loyalty.voucher.offerShared'));
            } else {
                // Fallback: Copy to clipboard
                await navigator.clipboard.writeText(shareText);
                notifyInfo('Tilbudsdetaljer kopieret til udklipsholder');
            }
        } catch (error) {
            if ((error as Error).name !== 'AbortError') {
                console.error('Share failed:', error);
                notifyInfo('Kunne ikke dele tilbud');
            }
        }
    };

    const handleCopyCode = async () => {
        try {
            const promoCodeElement = document.getElementById('promo-code');
            const textToCopy = promoCodeElement?.textContent || voucher.promoCode;

            if (textToCopy) {
                await navigator.clipboard.writeText(textToCopy);

                setCopied(true);
                notifyInfo('Kode kopieret til udklipsholder');

                setTimeout(() => {
                    setCopied(false);
                }, 1000);
            }
        } catch (error) {
            console.error('Failed to copy:', error);
            notifyInfo('Kunne ikke kopiere kode');
        }
    };

    return (
        <Drawer open={open} onClose={onClose} isInfo={true}>
            <div className={styles.drawerContainer}>
                {/* Header with close button */}
                <div className={styles.header}>
                    <button
                        className={styles.closeButton}
                        onClick={onClose}
                        aria-label={translate('loyalty.voucher.closeDrawer')}
                    >
                        ✕
                    </button>
                </div>

                {/* Voucher content */}
                <div className={styles.voucherContent}>
                    {voucher.badgeText && (
                        <div className={styles.badge}>
                            {translate(voucher.badgeText as TranslationKey)}
                        </div>
                    )}

                    <Typography as="h2" variant="title-XL" weight="medium" className={styles.title}>
                        {voucher.title}
                    </Typography>

                    <Typography
                        as="p"
                        variant="paragraph-S"
                        weight="regular"
                        className={styles.description}
                    >
                        {voucher.description}
                    </Typography>

                    <Typography
                        as="p"
                        variant="paragraph-S"
                        weight="regular"
                        className={styles.validityInfo}
                    >
                        Gyldig t.o.m {voucher.expiryDate}{' '}
                        <Anchor href="/" variant="textLink-S" className={styles.termsLink}>
                            {translate('common.readTerms')}
                        </Anchor>
                    </Typography>

                    {voucher.imageSrc && (
                        <div className={styles.imageContainer}>
                            <Image
                                src={voucher.imageSrc}
                                alt={voucher.imageAlt || 'Voucher illustration'}
                                width={120}
                                height={120}
                                className={styles.voucherImage}
                            />
                        </div>
                    )}
                </div>

                {/* Share voucher section */}
                <div className={clsx(styles.dropShadow, styles.shareVoucherContainer)}>
                    <Typography
                        as="h4"
                        variant="paragraph-M"
                        weight="medium"
                        className={styles.shareLabel}
                    >
                        {translate('loyalty.passOfferOn')}
                    </Typography>

                    <Button
                        variant="primary"
                        className={styles.shareButton}
                        onClick={handleShareVoucher}
                        type="button"
                        aria-label="Del tilbud"
                    >
                        <ShareIcon /> <span>Del tilbud</span>
                    </Button>
                </div>

                {/* Redemption options */}
                <div className={clsx(styles.dropShadow, styles.redemptionOption)}>
                    <div className={styles.redemptionOptionInfo}>
                        <Typography
                            as="h4"
                            variant="paragraph-M"
                            weight="medium"
                            className={styles.optionTitle}
                        >
                            {translate('loyalty.paymentInStore')}
                        </Typography>
                        <Typography
                            as="p"
                            variant="paragraph-S"
                            weight="regular"
                            className={styles.optionDescription}
                        >
                            {translate('loyalty.redeemCodeToCounter')}
                        </Typography>
                    </div>

                    {voucher.barcode && (
                        <div className={styles.qrCodeContainer}>
                            <QRCodeSVG
                                value={voucher.barcode}
                                size={120}
                                level="M"
                                className={styles.qrCode}
                            />
                        </div>
                    )}
                </div>

                {/* Online redemption */}
                <div className={clsx(styles.dropShadow, styles.redemptionOption)}>
                    <div className={styles.redemptionOptionInfo}>
                        <Typography
                            as="h4"
                            variant="paragraph-M"
                            weight="medium"
                            className={styles.optionTitle}
                        >
                            Brug på Imerco.dk
                        </Typography>
                        <Typography
                            as="p"
                            variant="paragraph-S"
                            weight="regular"
                            className={styles.optionDescription}
                        >
                            {translate('loyalty.useCodeOnCheckout')}
                        </Typography>
                    </div>

                    {voucher.promoCode && (
                        <div className={styles.promoCodeContainer}>
                            <div className={styles.promoCode} id="promo-code">
                                {voucher.promoCode}
                            </div>
                            <Button
                                variant="primary"
                                className={styles.copyButton}
                                onClick={handleCopyCode}
                                type="button"
                                aria-label={
                                    copied
                                        ? translate('common.copiedToClipboard')
                                        : translate('common.copyCode')
                                }
                                disabled={copied}
                            >
                                {copied
                                    ? translate('common.copiedToClipboard')
                                    : translate('common.copyCode')}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </Drawer>
    );
};

export default VouchersCardDrawer;
